/**
 * "Scheduled (N)" chip above the composer and the panel it opens: the caller's
 * scheduled messages for this channel, each with Edit (text and time), Send now
 * and Cancel. The list is the caller's own — the backend scopes it.
 */

import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Loader2, Pencil, Send, X } from 'lucide-react';
import { messagingApi, ScheduledMessage } from '@/services/messagingApi';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from '../ui/use-toast';
import { AutoGrowTextarea } from './AutoGrowTextarea';
import { ScheduleDateTimeFields, useScheduleErrorText } from './ScheduleSendMenu';
import {
  formatScheduleTime,
  fromDateTimeInputs,
  parseServerDate,
  toDateInputValue,
  toTimeInputValue,
  validateScheduledAt,
} from './scheduleSend';
import { ItemZoneNote } from '@/components/ui/ItemZoneNote';

export const scheduledMessagesKey = (channelId: number) => ['scheduled-messages', channelId] as const;

/** The caller's pending scheduled messages for a channel (soonest first). */
export function useScheduledMessages(channelId: number, enabled = true) {
  return useQuery({
    queryKey: scheduledMessagesKey(channelId),
    queryFn: async () => {
      const rows = await messagingApi.listScheduledMessages(channelId);
      return rows
        .filter((r) => r.channel_id == null || r.channel_id === channelId)
        .filter((r) => !r.status || r.status === 'scheduled' || r.status === 'failed')
        .sort((a, b) => (parseServerDate(a.scheduled_at)?.getTime() ?? 0) - (parseServerDate(b.scheduled_at)?.getTime() ?? 0));
    },
    enabled,
    // The server sends them on time; refresh now and then so sent ones drop off.
    refetchInterval: 60_000,
    retry: false,
  });
}

function ScheduledRow({
  item,
  channelId,
  onSentNow,
}: {
  item: ScheduledMessage;
  channelId: number;
  onSentNow?: () => void;
}) {
  const { t, language } = useAppTranslation();
  const errorText = useScheduleErrorText();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [content, setContent] = useState(item.content);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const at = parseServerDate(item.scheduled_at);

  const refresh = () => queryClient.invalidateQueries({ queryKey: scheduledMessagesKey(channelId) });
  const fail = (title: string) => (error: Error) =>
    toast({ variant: 'destructive', title, description: error?.message });

  const save = useMutation({
    mutationFn: (changes: { content?: string; scheduled_at?: string }) =>
      messagingApi.updateScheduledMessage(item.id, changes),
    onSuccess: () => {
      setEditing(false);
      refresh();
      toast({ title: t('messaging.schedule.updated', 'Scheduled message updated') });
    },
    onError: fail(t('messaging.schedule.updateFailed', 'Could not update the scheduled message')),
  });
  const sendNow = useMutation({
    mutationFn: () => messagingApi.sendScheduledMessageNow(item.id),
    onSuccess: () => {
      refresh();
      queryClient.invalidateQueries({ queryKey: ['channel-messages', channelId] });
      toast({ title: t('messaging.schedule.sentNow', 'Message sent') });
      onSentNow?.();
    },
    onError: fail(t('messaging.schedule.sendNowFailed', 'Could not send the message')),
  });
  const cancel = useMutation({
    mutationFn: () => messagingApi.cancelScheduledMessage(item.id),
    onSuccess: () => {
      refresh();
      toast({ title: t('messaging.schedule.cancelled', 'Scheduled message cancelled') });
    },
    onError: fail(t('messaging.schedule.cancelFailed', 'Could not cancel the scheduled message')),
  });
  const busy = save.isPending || sendNow.isPending || cancel.isPending;

  const startEdit = () => {
    const base = at ?? new Date(Date.now() + 60 * 60 * 1000);
    setContent(item.content);
    setDate(toDateInputValue(base));
    setTime(toTimeInputValue(base));
    setSubmitted(false);
    setEditing(true);
  };

  const when = fromDateTimeInputs(date, time);
  const timeChanged = !!when && (!at || Math.abs(when.getTime() - at.getTime()) >= 60_000);
  const timeErr = timeChanged ? validateScheduledAt(when, new Date()) : when ? null : 'invalid';
  const submitEdit = () => {
    setSubmitted(true);
    if (timeErr || !content.trim()) return;
    const changes: { content?: string; scheduled_at?: string } = {};
    if (content !== item.content) changes.content = content;
    if (timeChanged && when) changes.scheduled_at = when.toISOString();
    if (Object.keys(changes).length === 0) {
      setEditing(false);
      return;
    }
    save.mutate(changes);
  };

  return (
    <li className="rounded-lg border p-3" data-testid={`scheduled-item-${item.id}`}>
      {editing ? (
        <form
          noValidate
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            submitEdit();
          }}
        >
          <AutoGrowTextarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onSubmit={submitEdit}
            aria-label={t('messaging.schedule.editContent', 'Message')}
            data-testid="scheduled-edit-content"
          />
          <ScheduleDateTimeFields
            idPrefix={`scheduled-${item.id}`}
            date={date}
            time={time}
            onDateChange={setDate}
            onTimeChange={setTime}
            error={submitted ? errorText(timeErr) : null}
          />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setEditing(false)} disabled={busy}>
              {t('messaging.schedule.back', 'Back')}
            </Button>
            <Button type="submit" size="sm" disabled={busy || !content.trim()} data-testid="scheduled-edit-save">
              {save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('messaging.channel.save', 'Save')}
            </Button>
          </div>
        </form>
      ) : (
        <>
          <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <CalendarClock className="h-3.5 w-3.5" />
            <span data-testid="scheduled-item-time">
              {at
                ? t('messaging.schedule.sendsAt', 'Sends {{when}}', { when: formatScheduleTime(at, language) })
                : item.scheduled_at}
              {at && <ItemZoneNote value={at} itemTimeZone={item.timezone} kind="scheduled" className="ml-1" />}
            </span>
            {item.status === 'failed' && (
              <Badge variant="danger" className="text-[10px]">
                {t('messaging.schedule.statusFailed', 'Failed')}
              </Badge>
            )}
            {(item.priority ?? item.metadata?.priority) === 'urgent' && (
              <Badge variant="outline" className="border-red-300 bg-red-50 text-[10px] text-red-700">
                {t('messaging.urgent.label', 'Urgent')}
              </Badge>
            )}
          </div>
          <p className="line-clamp-3 whitespace-pre-wrap break-words text-sm">{item.content}</p>
          <div className="mt-2 flex flex-wrap justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" className="gap-1" onClick={startEdit} disabled={busy} data-testid="scheduled-edit">
              <Pencil className="h-3.5 w-3.5" />
              {t('messaging.schedule.edit', 'Edit')}
            </Button>
            <Button type="button" variant="outline" size="sm" className="gap-1" onClick={() => sendNow.mutate()} disabled={busy} data-testid="scheduled-send-now">
              {sendNow.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              {t('messaging.schedule.sendNow', 'Send now')}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="gap-1 text-destructive hover:text-destructive"
              onClick={() => cancel.mutate()}
              disabled={busy}
              data-testid="scheduled-cancel"
            >
              <X className="h-3.5 w-3.5" />
              {t('messaging.schedule.cancel', 'Cancel')}
            </Button>
          </div>
        </>
      )}
    </li>
  );
}

interface ScheduledMessagesChipProps {
  channelId: number;
  enabled?: boolean;
  /** A scheduled message went out now — e.g. scroll the thread to the bottom. */
  onSentNow?: () => void;
}

export function ScheduledMessagesChip({ channelId, enabled = true, onSentNow }: ScheduledMessagesChipProps) {
  const { t } = useAppTranslation();
  const [open, setOpen] = useState(false);
  const { data: items = [] } = useScheduledMessages(channelId, enabled);
  if (items.length === 0 && !open) return null;

  return (
    <>
      <div className="flex-shrink-0 px-4 pt-2">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700 hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-200"
          data-testid="scheduled-chip"
        >
          <CalendarClock className="h-3.5 w-3.5" />
          {t('messaging.schedule.chip', 'Scheduled ({{count}})', { count: items.length })}
        </button>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="scheduled-panel">
          <DialogHeader>
            <DialogTitle>{t('messaging.schedule.panelTitle', 'Scheduled messages')}</DialogTitle>
            <DialogDescription>
              {t('messaging.schedule.panelHint', 'Only you can see these until they are sent.')}
            </DialogDescription>
          </DialogHeader>
          {items.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              {t('messaging.schedule.panelEmpty', 'No scheduled messages in this conversation.')}
            </p>
          ) : (
            <ul className="max-h-[60vh] space-y-2 overflow-y-auto">
              {items.map((item) => (
                <ScheduledRow key={item.id} item={item} channelId={channelId} onSentNow={onSentNow} />
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ScheduledMessagesChip;
